"use client"

import { useCallback, useEffect, useState } from "react"
import { useApi } from "@/lib/api"
import { coalesceWorkflowFetch } from "@/hooks/workflow-fetch-cache"
import type { StageConfiguration, WorkflowConfiguration } from "@/types/workflow"

/**
 * Configuration descriptor for the AtsPlacementRequest tab.
 *
 * Mirrors {@link useClientRequestConfig}: ATS is a Requests-group entity on the
 * shared gw-client-request-index, so it uses the same field-config-driven shape
 * (single stage, no FixedFilter). The backend lazily materialises a default view
 * from the seeded AtsPlacementRequest FieldConfigurations on first read.
 */
export interface AtsConfig {
  entityType: "AtsPlacementRequest"
  stage: StageConfiguration | null
  configuration: WorkflowConfiguration | null
  /** Total ATS row count for the tab pill; null while in-flight or on failure. */
  count: number | null
}

/**
 * Fetches AtsPlacementRequest stage configuration + total row count via the same
 * shared endpoints CR/VMS use (configurations + v2/search). No bespoke ATS calls.
 */
export function useAtsConfig() {
  const { workflowApi } = useApi()
  const [config, setConfig] = useState<AtsConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchConfig = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const entityType: AtsConfig["entityType"] = "AtsPlacementRequest"
      const params = new URLSearchParams({ entityType })
      const configUrl = `/api/workflow/v2/configurations?${params.toString()}`

      const [resolvedConfig, count] = await Promise.all([
        coalesceWorkflowFetch(configUrl, async () => {
          const c = await workflowApi.get<WorkflowConfiguration>(configUrl)
          if (c && Array.isArray(c.stageConfigurations)) {
            c.stageConfigurations.forEach((stageConfig) => {
              if (Array.isArray(stageConfig.fieldConfigurations)) {
                stageConfig.fieldConfigurations.sort((a, b) => a.order - b.order)
              }
            })
            c.stageConfigurations.sort((a, b) => a.order - b.order)
          }
          return c
        }),
        workflowApi
          .post<any>("/api/workflow/v2/search", {
            entityType,
            top: 0,
            skip: 0,
            search: "*",
            count: true,
            select: "id",
          })
          .then((r: any) => {
            const c = r?.["@odata.count"] ?? r?.count ?? r?.totalCount
            return typeof c === "number" ? c : null
          })
          .catch(() => null),
      ])

      setConfig({
        entityType,
        stage: resolvedConfig?.stageConfigurations?.[0] ?? null,
        configuration: resolvedConfig ?? null,
        count,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to fetch AtsPlacementRequest configuration"
      setError(msg)
      setConfig(null)
    } finally {
      setIsLoading(false)
    }
  }, [workflowApi])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return {
    config,
    isLoading,
    error,
    refetch: fetchConfig,
  }
}
